import { Autocomplete, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import loadGoogleMaps from '../../utils/googleMapsLoader';
import useDebounce from '../../utils/useDebounce';

PlacesAutocomplete.propTypes = {
  value: PropTypes.string,
  displayValue: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  sx: PropTypes.object,
};

export default function PlacesAutocomplete({
  value = '',
  displayValue,
  onChange,
  label = 'Enter a location',
  disabled = false,
  size = 'small',
  sx,
}) {
  const [inputValue, setInputValue] = useState(displayValue ?? value ?? '');
  const [options, setOptions] = useState([]);
  const [hasServiceError, setHasServiceError] = useState(false);
  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let active = true;
    loadGoogleMaps()
      .then((google) => {
        if (!active) return;
        placesLibraryRef.current = google.maps.places;
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      })
      .catch(() => {
        if (active) {
          placesLibraryRef.current = null;
          setHasServiceError(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setInputValue(displayValue ?? value ?? '');
  }, [displayValue, value]);

  const debouncedFetchPredictions = useDebounce(async (input) => {
    const requestId = ++requestIdRef.current;
    const normalizedInput = input.trim();
    if (!placesLibraryRef.current || normalizedInput.length < 3) {
      setOptions([]);
      return;
    }
    try {
      const { suggestions } = await placesLibraryRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: normalizedInput,
        includedRegionCodes: ['us'],
        sessionToken: sessionTokenRef.current,
      });
      if (requestId === requestIdRef.current) {
        setHasServiceError(false);
        setOptions(suggestions || []);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setHasServiceError(true);
        setOptions([]);
      }
    }
  }, 300);

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.placePrediction?.text?.text || '')}
      isOptionEqualToValue={(option, selected) =>
        option?.placePrediction?.placeId === selected?.placePrediction?.placeId
      }
      value={inputValue}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input' || reason === 'clear') {
          requestIdRef.current += 1;
          if (reason === 'input') debouncedFetchPredictions(newInputValue);
          if (onChange) onChange(null);
        }
      }}
      onChange={async (event, selected) => {
        const description = typeof selected === 'string' ? selected : selected?.placePrediction?.text?.text || '';
        setInputValue(description);
        setOptions([]);
        const selectionRequestId = ++requestIdRef.current;
        if (placesLibraryRef.current) {
          sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
        }
        const prediction = typeof selected === 'string' ? null : selected;
        if (!prediction) {
          return;
        }

        const place = prediction.placePrediction?.toPlace?.();
        try {
          await place?.fetchFields({ fields: ['addressComponents'] });
          if (selectionRequestId !== requestIdRef.current) return;

          const addressComponents = place?.addressComponents ?? [];
          const getComponent = (type) =>
            addressComponents.find((component) => component.types?.includes(type))?.longText || null;
          const streetNumber = getComponent('street_number');
          const route = getComponent('route');
          const subpremise = getComponent('subpremise');

          if (onChange) {
            onChange({
              address: [streetNumber, route, subpremise].filter(Boolean).join(' ') || description,
              displayValue: description,
              city: getComponent('locality') || getComponent('sublocality_level_1'),
              state: getComponent('administrative_area_level_1'),
            });
          }
        } catch {
          if (onChange) {
            onChange({ address: description, displayValue: description, city: null, state: null });
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          fullWidth
          autoComplete="off"
          error={hasServiceError}
          helperText={hasServiceError ? 'Location suggestions are temporarily unavailable. Try again later.' : ''}
          sx={sx}
        />
      )}
    />
  );
}
